import { useState } from 'react'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import { ScrollArea } from '@/components/ui/scroll-area'
import { Input } from '@/components/ui/input'
import { Badge } from '@/components/ui/badge'
import { useAuth } from '@/hooks/useAuth'
import { useAllBroadcastDevices } from '@/hooks/useAllBroadcastDevices'
import { tvTemplateService } from '@/services/tv_template_service'
import { Loader2, Search, Check, Monitor } from 'lucide-react'
import { logger } from '@magnetar/utils'

interface ApplyDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  templateId: number
  appliedDeviceIds: number[]
  onSuccess?: () => void
}

export default function ApplyDialog({
  open,
  onOpenChange,
  templateId,
  appliedDeviceIds,
  onSuccess,
}: ApplyDialogProps) {
  const { token } = useAuth()
  const { devices: broadcastDevices = [] } = useAllBroadcastDevices()
  const [selectedDeviceIds, setSelectedDeviceIds] = useState<Set<number>>(new Set())
  const [isApplying, setIsApplying] = useState(false)
  const [searchTerm, setSearchTerm] = useState('')

  const appliedSet = new Set(appliedDeviceIds)

  const handleToggleDevice = (deviceId: number) => {
    const newSelected = new Set(selectedDeviceIds)
    if (newSelected.has(deviceId)) {
      newSelected.delete(deviceId)
    } else {
      newSelected.add(deviceId)
    }
    setSelectedDeviceIds(newSelected)
  }

  const handleApply = async () => {
    if (!token || selectedDeviceIds.size === 0) return

    setIsApplying(true)
    try {
      const result = await tvTemplateService.applyTemplate(
        templateId,
        Array.from(selectedDeviceIds),
        token,
      )
      if (result.success) {
        setSelectedDeviceIds(new Set())
        onOpenChange(false)
        onSuccess?.()
      }
    } catch (error) {
      logger.error('套用失敗', error)
    } finally {
      setIsApplying(false)
    }
  }

  // Group devices by clinic
  const devicesByClinic = broadcastDevices.reduce(
    (acc, device) => {
      const clinicName = device.clinic?.clinic_name || '未知診所'
      if (!acc[clinicName]) {
        acc[clinicName] = []
      }
      acc[clinicName].push(device)
      return acc
    },
    {} as Record<string, typeof broadcastDevices>,
  )

  const filteredClinicNames = Object.keys(devicesByClinic)
    .filter((clinicName) => {
      const devices = devicesByClinic[clinicName]
      return (
        clinicName.toLowerCase().includes(searchTerm.toLowerCase()) ||
        devices.some((d) => d.name?.toLowerCase().includes(searchTerm.toLowerCase()))
      )
    })
    .sort()

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[600px]">
        <DialogHeader>
          <DialogTitle>套用模板到設備</DialogTitle>
          <DialogDescription>選擇要套用此模板的廣播設備</DialogDescription>
        </DialogHeader>

        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="搜尋診所或設備..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10"
          />
        </div>

        <ScrollArea className="h-[400px]">
          <div className="space-y-4">
            {filteredClinicNames.map((clinicName) => {
              const devices = devicesByClinic[clinicName].filter(
                (d) =>
                  clinicName.toLowerCase().includes(searchTerm.toLowerCase()) ||
                  d.name?.toLowerCase().includes(searchTerm.toLowerCase()),
              )

              if (devices.length === 0) return null

              return (
                <div key={clinicName} className="space-y-2">
                  <h4 className="font-medium text-sm text-muted-foreground">{clinicName}</h4>
                  {devices.map((device) => {
                    const isApplied = appliedSet.has(device.broadcast_device_id)
                    return (
                      <div
                        key={device.broadcast_device_id}
                        className={`flex items-center space-x-3 p-3 rounded-lg border ${
                          isApplied ? 'bg-muted/50' : 'hover:bg-muted/50 cursor-pointer'
                        }`}
                        onClick={() =>
                          !isApplied && handleToggleDevice(device.broadcast_device_id)
                        }
                      >
                        <Checkbox
                          checked={
                            selectedDeviceIds.has(device.broadcast_device_id) || isApplied
                          }
                          disabled={isApplied}
                          onCheckedChange={() =>
                            handleToggleDevice(device.broadcast_device_id)
                          }
                          onClick={(e) => e.stopPropagation()}
                        />
                        <Monitor className="h-4 w-4 text-muted-foreground" />
                        <div className="flex-1">
                          <p className="font-medium">
                            {device.name || `設備 ${device.broadcast_device_id}`}
                          </p>
                        </div>
                        {isApplied && (
                          <Badge variant="secondary">
                            <Check className="mr-1 h-3 w-3" />
                            已套用
                          </Badge>
                        )}
                      </div>
                    )
                  })}
                </div>
              )
            })}
          </div>
        </ScrollArea>

        <DialogFooter>
          <div className="flex w-full justify-between items-center">
            <p className="text-sm text-muted-foreground">
              已選擇 {selectedDeviceIds.size} 台設備
            </p>
            <div className="flex gap-2">
              <Button
                variant="outline"
                onClick={() => onOpenChange(false)}
                disabled={isApplying}
              >
                取消
              </Button>
              <Button
                onClick={handleApply}
                disabled={isApplying || selectedDeviceIds.size === 0}
              >
                {isApplying && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                套用模板
              </Button>
            </div>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
